import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { KeyValueSchema } from "./shared/key-value-overrides"
import type { RouteStatus } from "@/api/admin-api"

import { EFFORT_LEVELS } from "@/api/admin-api"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useAvailableGPTModels } from "@/hooks/use-config-query"

import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import { KeyValueOverrides } from "./shared/key-value-overrides"
import { RawJsonTab } from "./shared/raw-json-tab"

type EditRouteDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  route: RouteStatus | null
}

type StoredRoute = {
  id?: string
  name?: string
  description?: string
  enabled?: boolean
  access?: Record<string, unknown>
  backend_overrides?: Record<string, unknown>
  overrides?: Record<string, unknown>
  [key: string]: unknown
}

// Radix Select cannot hold an empty value, so "not set" gets a sentinel.
const UNSET = "__unset__"

function stringOverride(overrides: Record<string, unknown> | undefined, key: string): string {
  const value = overrides?.[key]
  return typeof value === "string" ? value.trim() : ""
}

/**
 * Known keys inside ``backend_overrides`` that already have meaningful
 * semantics in the backend. ``KeyValueOverrides`` uses these hints to
 * render typed controls (switch / number / string) instead of raw string
 * inputs, and surfaces them in the "Or add a known key" dropdown.
 *
 * ``custom_instructions`` is handled by a dedicated textarea outside the
 * KeyValueOverrides block, so it's not in this schema.
 */
const BACKEND_OVERRIDES_SCHEMA: KeyValueSchema = {
  disableWebSearch: {
    type: "boolean",
    label: "disableWebSearch",
    description: "Block this route from using the web search tool.",
  },
  slide_sense_stale_window_days: {
    type: "number",
    label: "Dead-items window (days)",
    description:
      "SlideSense only. Default window (1–365) for the admin Inactive Slides / Inactive Presentations report. Leave empty to use 90 days.",
  },
}

export function EditRouteDialog({ isOpen, onOpenChange, route }: EditRouteDialogProps) {
  const { t } = useTranslation()
  const routeId = route?.id ?? null
  const endpoint = routeId ? `/admin/routes/${encodeURIComponent(routeId)}/raw` : null

  // Full stored route object + hash. The list row (`route` prop) only has
  // id / name / status / modules; the full doc lives here.
  const {
    data: rawData,
    isLoading,
    refetch,
  } = useRawEntityQuery<StoredRoute>(endpoint, isOpen && !!endpoint)

  const saveMutation = useSaveRawEntityMutation<StoredRoute>({
    endpoint: endpoint ?? "",
    invalidateOnSuccess: [["admin", "routes"]],
  })

  const stored: StoredRoute = useMemo(() => rawData?.entity ?? {}, [rawData])
  const hash = rawData?.hash ?? ""

  const [customInstructions, setCustomInstructions] = useState("")
  const [overrides, setOverrides] = useState<Record<string, unknown>>({})
  // The route's default model and effort (#3327) live in the route's
  // client-visible `overrides`, not in backend_overrides: the composer
  // preselects them.
  const [defaultModelId, setDefaultModelId] = useState("")
  const [defaultReasoningEffort, setDefaultReasoningEffort] = useState("")
  const { models } = useAvailableGPTModels("explorer")
  const modelOptions = useMemo(() => models.filter((model) => model.id !== "auto"), [models])
  const [resetSignal, setResetSignal] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)

  // Sync local Form state whenever the server returns a new entity.
  useEffect(() => {
    const be = (stored.backend_overrides ?? {}) as Record<string, unknown>
    const { custom_instructions: ci, ...rest } = be
    setCustomInstructions(typeof ci === "string" ? ci : "")
    setOverrides(rest)
    setDefaultModelId(stringOverride(stored.overrides, "defaultModelId"))
    setDefaultReasoningEffort(stringOverride(stored.overrides, "defaultReasoningEffort"))
    setResetSignal((n) => n + 1)
  }, [stored])

  useEffect(() => {
    if (!isOpen) {
      setFormError(null)
      setCustomInstructions("")
      setOverrides({})
      setDefaultModelId("")
      setDefaultReasoningEffort("")
    }
  }, [isOpen])

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!routeId || !endpoint || !hash) return
    setFormError(null)

    // Merge form state into the current stored object. We only touch
    // backend_overrides and the two model/effort keys in overrides;
    // everything else (access, enabled, name, other overrides) stays as-is
    // so a Form-tab save is a targeted edit.
    const nextBackendOverrides: Record<string, unknown> = { ...overrides }
    if (customInstructions.trim()) {
      nextBackendOverrides.custom_instructions = customInstructions.trim()
    } else {
      delete nextBackendOverrides.custom_instructions
    }

    const nextRouteOverrides: Record<string, unknown> = { ...(stored.overrides ?? {}) }
    for (const [key, value] of [
      ["defaultModelId", defaultModelId],
      ["defaultReasoningEffort", defaultReasoningEffort],
    ] as const) {
      if (value) {
        nextRouteOverrides[key] = value
      } else {
        delete nextRouteOverrides[key]
      }
    }
    // Keys of the 7.13.0 model lock (#3307), which nothing reads any more.
    delete nextRouteOverrides.forceModelId
    delete nextRouteOverrides.minReasoningEffort

    const nextEntity: Record<string, unknown> = {
      ...stored,
      backend_overrides: nextBackendOverrides,
      overrides: nextRouteOverrides,
    }

    try {
      await saveMutation.mutateAsync({
        entity: nextEntity,
        expectedHash: hash,
        reason: t("adminDashboard.routeEdit.reasonForm", {
          defaultValue: "Form-tab edit of route {{id}} backend overrides",
          id: routeId,
        }),
        source: "form",
      })
      void refetch()
      toast.success(t("adminDashboard.routeEdit.toastSaved", { defaultValue: "Route updated" }))
      onOpenChange(false)
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.routeEdit.errorSaveFailed", { defaultValue: "Save failed" })
      )
    }
  }

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!routeId || !endpoint || !hash) return
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await saveMutation.mutateAsync({
        entity: parsed,
        expectedHash: hash,
        reason,
        source: "raw",
      })
      void refetch()
      toast.success(
        t("adminDashboard.routeEdit.toastSavedRaw", { defaultValue: "Route saved (raw)" })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.routeEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!route) return null

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.routeEdit.title", { defaultValue: "Edit route" })}{" "}
            <code className="font-mono text-sm">{route.id}</code>{" "}
            <span className="text-muted-foreground text-sm font-normal">({route.name})</span>
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.routeEdit.description", {
              defaultValue:
                "Manage this route's default model and effort and its backend-only overrides. Status, access policies, and module list are edited from the routes table itself. The Raw tab shows the full stored route object.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.routeEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.routeEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4 max-h-[60vh] overflow-y-auto" value="form">
            <form className="space-y-5" onSubmit={handleFormSubmit}>
              <div className="space-y-2">
                <Label>
                  {t("adminDashboard.routeEdit.fieldRouteId", { defaultValue: "Route ID" })}
                </Label>
                <Input disabled className="font-mono text-xs" value={route.id} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="route-instructions">
                  {t("adminDashboard.routeEdit.fieldCustomInstructions", {
                    defaultValue: "Custom instructions",
                  })}
                </Label>
                <Textarea
                  id="route-instructions"
                  rows={8}
                  value={customInstructions}
                  placeholder={t("adminDashboard.routeEdit.fieldCustomInstructionsPlaceholder", {
                    defaultValue:
                      "Optional — prepended to the system prompt when this route is active.",
                  })}
                  onChange={(event) => setCustomInstructions(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.routeEdit.fieldCustomInstructionsHelp", {
                    defaultValue:
                      "Leaving this empty clears the override. Recommended length: under 2000 characters.",
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="route-default-model">
                  {t("adminDashboard.routeEdit.fieldDefaultModel", {
                    defaultValue: "Default model",
                  })}
                </Label>
                <Select
                  disabled={saveMutation.isPending}
                  value={defaultModelId || UNSET}
                  onValueChange={(value) => setDefaultModelId(value === UNSET ? "" : value)}
                >
                  <SelectTrigger id="route-default-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNSET}>
                      {t("adminDashboard.routeEdit.fieldDefaultModelUnset", {
                        defaultValue: "Not set (Auto)",
                      })}
                    </SelectItem>
                    {/* Keep a stored id selectable even when it is not in the list. */}
                    {defaultModelId &&
                    !modelOptions.some((model) => model.id === defaultModelId) ? (
                      <SelectItem value={defaultModelId}>{defaultModelId}</SelectItem>
                    ) : null}
                    {modelOptions.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.display_name || model.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.routeEdit.fieldDefaultModelHelp", {
                    defaultValue:
                      "Preselected for every new chat on this route. Users can still pick another model, including Auto. Users who cannot use this model start on Auto.",
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="route-default-effort">
                  {t("adminDashboard.routeEdit.fieldDefaultEffort", {
                    defaultValue: "Default reasoning effort",
                  })}
                </Label>
                <Select
                  disabled={saveMutation.isPending}
                  value={defaultReasoningEffort || UNSET}
                  onValueChange={(value) => setDefaultReasoningEffort(value === UNSET ? "" : value)}
                >
                  <SelectTrigger id="route-default-effort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNSET}>
                      {t("adminDashboard.routeEdit.fieldDefaultEffortUnset", {
                        defaultValue: "Not set",
                      })}
                    </SelectItem>
                    {EFFORT_LEVELS.map((effort) => (
                      <SelectItem key={effort} value={effort}>
                        {effort}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.routeEdit.fieldDefaultEffortHelp", {
                    defaultValue:
                      "Preselected with the default model, snapped to the model's highest level at or below it. Users can still change it.",
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <KeyValueOverrides
                  disabled={saveMutation.isPending}
                  schema={BACKEND_OVERRIDES_SCHEMA}
                  value={overrides}
                  description={t("adminDashboard.routeEdit.overridesDescription", {
                    defaultValue:
                      "Additional backend_overrides keys. Known keys render with typed controls; anything else falls through to a string input. Complex values need Raw mode.",
                  })}
                  label={t("adminDashboard.routeEdit.overridesLabel", {
                    defaultValue: "Backend overrides (other)",
                  })}
                  onChange={setOverrides}
                />
              </div>

              {formError ? <p className="text-destructive text-xs">{formError}</p> : null}

              <DialogFooter>
                <Button
                  disabled={saveMutation.isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.routeEdit.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button disabled={saveMutation.isPending || isLoading || !hash} type="submit">
                  {saveMutation.isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.routeEdit.saving", { defaultValue: "Saving…" })}
                    </>
                  ) : (
                    t("adminDashboard.routeEdit.save", { defaultValue: "Save" })
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={route.id}
              isPending={saveMutation.isPending || !hash}
              resetSignal={resetSignal}
              value={stored}
              resourceLabel={t("adminDashboard.routeEdit.rawResourceLabel", {
                defaultValue: "route {{id}}",
                id: route.id,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
